import React, { useEffect, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
    Avatar,
    Chip,
    TablePagination,
    Divider,
    TextField,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    InputAdornment,
    Alert,
    CircularProgress,
    Fade,
} from "@mui/material";
import {
    Refresh,
    Search,
    Person,
    AdminPanelSettings,
    VisibilityOutlined,
    Edit,
    Delete,
    Warning,
    Clear,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import axiosInstance from "../../../services/axiosInstance";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import TeamMemberModal from "./TeamMemberModal";

/* ---------------- Styled Components ---------------- */

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    backgroundColor: "#f4f7fc",
    color: "#334155",
    fontWeight: 700,
    fontSize: "0.875rem",
    padding: "16px",
    borderBottom: "2px solid #e2e8f0",
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
        backgroundColor: theme.palette.action.hover,
    },
    "&:hover": {
        backgroundColor: "#f1f5f9",
        transition: "background-color 0.2s ease",
    },
    "& td": {
        padding: "16px",
        borderColor: "#f1f5f9",
    },
}));

const RoleChip = ({ role }) => {
    const config = {
        admin: { label: "Admin", color: "#6366f1", bgcolor: "#eef2ff", icon: <AdminPanelSettings fontSize="small" /> },
        member: { label: "Member", color: "#10b981", bgcolor: "#ecfdf5", icon: <Person fontSize="small" /> },
        viewer: { label: "Viewer", color: "#64748b", bgcolor: "#f8fafc", icon: <VisibilityOutlined fontSize="small" /> },
        "account manager": { label: "Account Manager", color: "#9c27b0", bgcolor: "#f3e5f5", icon: <Person fontSize="small" /> },
    };
    const { label, color, bgcolor, icon } = config[role] || config.member;

    return (
        <Chip
            icon={React.cloneElement(icon, { style: { color } })}
            label={label}
            size="small"
            sx={{
                fontWeight: 600,
                color,
                bgcolor,
                borderRadius: "6px",
                "& .MuiChip-label": { px: 1 },
            }}
        />
    );
};

/* -------------------- Main Component -------------------- */

const TeamMembersTable = ({ refreshTrigger, onMembersCountChange }) => {
    const [teamMemberData, setTeamMemberData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [memberToDelete, setMemberToDelete] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [memberToEdit, setMemberToEdit] = useState(null);

    const clientId = localStorage.getItem("userId");
    const userRole = localStorage.getItem("userRole");

    const getMembersDetails = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const response = await axiosInstance.get(
                `${apiEndPointsConfig.getTeamMembersByClientId}/me`
            );

            if (response.data.success) {
                setTeamMemberData(response.data.data);
                setFilteredData(response.data.data);
                if (onMembersCountChange) {
                    onMembersCountChange(response.data.data.length);
                }
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            setMessage({ type: "error", text: "Failed to load team members." });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (clientId) getMembersDetails();
    }, [clientId, refreshTrigger]);

    const handleDeleteClick = (member) => {
        setMemberToDelete(member);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await axiosInstance.delete(
                `${apiEndPointsConfig.deleteTeamMember}/${memberToDelete._id}`
            );
            if (response.data.success) {
                getMembersDetails();
                setDeleteDialogOpen(false);
                setMessage({ type: "success", text: "Team member deleted successfully." });
            }
        } catch (error) {
            console.error("Error deleting member:", error);
            setMessage({ type: "error", text: error.response?.data?.message || "Failed to delete team member." });
        } finally {
            setMemberToDelete(null);
        }
    };

    const handleEditClick = (member) => {
        setMemberToEdit(member);
        setEditModalOpen(true);
    };

    useEffect(() => {
        if (!searchQuery) {
            setFilteredData(teamMemberData);
        } else {
            const lowerQuery = searchQuery.toLowerCase();
            const filtered = teamMemberData.filter(
                (m) =>
                    m.name.toLowerCase().includes(lowerQuery) ||
                    m.email.toLowerCase().includes(lowerQuery)
            );
            setFilteredData(filtered);
            setPage(0);
        }
    }, [searchQuery, teamMemberData]);

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Paper elevation={0} sx={{ borderRadius: 4, overflow: "hidden", border: "1px solid #e2e8f0" }}>
            {/* Table Header Controls */}
            <Box p={3} display="flex" justifyContent="space-between" alignItems="center" bgcolor="white">
                <Typography variant="h6" fontWeight={800} color="#1e293b">
                    All Members
                    <Chip
                        label={filteredData.length}
                        size="small"
                        sx={{ ml: 1.5, bgcolor: "#f1f5f9", fontWeight: 700, color: "#64748b" }}
                    />
                </Typography>

                <Box display="flex" gap={2} alignItems="center">
                    <TextField
                        size="small"
                        placeholder="Search members..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{
                            width: 300,
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 2.5,
                                bgcolor: "#f8fafc",
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: searchQuery && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearchQuery("")}>
                                        <Clear fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Tooltip title="Refresh List">
                        <IconButton onClick={getMembersDetails} sx={{ bgcolor: "#f8fafc", p: 1 }}>
                            <Refresh fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <Divider />

            {/* Alert Message */}
            {message.text && (
                <Box p={2}>
                    <Alert severity={message.type} onClose={() => setMessage({ type: "", text: "" })}>
                        {message.text}
                    </Alert>
                </Box>
            )}

            <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>Name</StyledTableCell>
                            <StyledTableCell>Role</StyledTableCell>
                            <StyledTableCell>Status</StyledTableCell>
                            <StyledTableCell>Created</StyledTableCell>
                            <StyledTableCell>Last Login</StyledTableCell>
                            {userRole !== "viewer" && <StyledTableCell align="right">Actions</StyledTableCell>}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {loading && teamMemberData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                                    <CircularProgress size={32} />
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                        Loading team members...
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : filteredData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                                    <Box sx={{ opacity: 0.5 }}>
                                        <Person sx={{ fontSize: 64, color: "text.secondary" }} />
                                    </Box>
                                    <Typography variant="h6" color="text.secondary" mt={2}>
                                        No members found
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredData
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((m) => (
                                    <StyledTableRow key={m._id}>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={2}>
                                                <Avatar
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        bgcolor: "#6366f1",
                                                        fontSize: "1rem",
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {m.name.charAt(0)}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={700} color="#1e293b">
                                                        {m.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {m.email}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <RoleChip role={m.role} />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={m.isActive ? "Active" : "Inactive"}
                                                size="small"
                                                sx={{
                                                    height: 24,
                                                    fontSize: "0.75rem",
                                                    fontWeight: 700,
                                                    bgcolor: m.isActive ? "#dcfce7" : "#fee2e2",
                                                    color: m.isActive ? "#166534" : "#991b1b",
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="#64748b">
                                                {new Date(m.createdAt).toLocaleDateString()}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="#64748b">
                                                {m.lastLogin
                                                    ? new Date(m.lastLogin).toLocaleDateString()
                                                    : "Never"}
                                            </Typography>
                                        </TableCell>
                                        {userRole !== "viewer" && (
                                            <TableCell align="right">
                                                <Box display="flex" justifyContent="flex-end" gap={0.5}>
                                                    <Tooltip title="Edit Member">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleEditClick(m)}
                                                            sx={{ color: "#4a90e2", "&:hover": { bgcolor: "#ebf5ff" } }}
                                                        >
                                                            <Edit fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete Member">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDeleteClick(m)}
                                                            sx={{ color: "#ef4444", "&:hover": { bgcolor: "#fef2f2" } }}
                                                        >
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        )}
                                    </StyledTableRow>
                                ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                TransitionComponent={Fade}
                PaperProps={{ sx: { borderRadius: 4, p: 1, maxWidth: 400 } }}
            >
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
                    <Warning sx={{ color: "#ef4444" }} />
                    <Typography variant="h6" fontWeight={800}>Confirm Delete</Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" color="text.secondary">
                        Are you sure you want to delete <b>{memberToDelete?.name}</b>? This action will permanently remove their access to the workspace.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} color="inherit" sx={{ fontWeight: 600 }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        color="error"
                        sx={{ borderRadius: 2.5, px: 3, fontWeight: 700, textTransform: "none", boxShadow: "none" }}
                    >
                        Delete Member
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Modal */}
            {memberToEdit && (
                <TeamMemberModal
                    open={editModalOpen}
                    onClose={() => {
                        setEditModalOpen(false);
                        setMemberToEdit(null);
                    }}
                    clientId={clientId}
                    onSuccess={() => {
                        getMembersDetails();
                        setEditModalOpen(false);
                        setMemberToEdit(null);
                    }}
                    initialValues={{
                        id: memberToEdit._id,
                        name: memberToEdit.name,
                        email: memberToEdit.email,
                        role: memberToEdit.role,
                        password: "",
                    }}
                    isEdit={true}
                />
            )}
        </Paper>
    );
};

export default TeamMembersTable;
